import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";

import { primaryNav } from "@/content/site";

export type MobileNavProps = {
  isOpen: boolean;
  onClose: () => void;
  panelId: string;
};

export function MobileNav({ isOpen, onClose, panelId }: MobileNavProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const firstLinkRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overflow;
    if (isOpen) {
      root.style.overflow = "hidden";
      const t = window.setTimeout(() => firstLinkRef.current?.focus(), 0);
      return () => {
        root.style.overflow = previous;
        window.clearTimeout(t);
      };
    }
    return () => {
      root.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <div
      id={panelId}
      ref={panelRef}
      className={`lg:hidden fixed inset-x-0 top-[75px] bottom-0 z-40
        transition-[opacity,transform] duration-150
        ${
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      style={{ backgroundColor: "#0a0a0a", color: "#fff" }}
      aria-hidden={!isOpen}
    >
      <nav aria-label="Mobile primary" className="container-page py-10 flex flex-col h-full">
        <ul className="flex flex-col gap-3">
          {primaryNav.map((item, i) => (
            <li key={item.href}>
              <NavLink
                ref={i === 0 ? firstLinkRef : undefined}
                to={item.href}
                end={!item.matchPrefix && item.href !== "/"}
                onClick={onClose}
                className={({ isActive }) =>
                  [
                    "block w-full px-5 py-4 text-xl font-semibold no-underline",
                    "min-h-[56px]",
                    isActive
                      ? "text-white border-l-[3px] border-[#d71920]"
                      : "text-white/90 hover:bg-white/5",
                  ].join(" ")
                }
                style={({ isActive }) => isActive ? { backgroundColor: "rgba(215,25,32,0.15)" } : undefined}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col gap-3">
          <NavLink to="/join" onClick={onClose} className="btn-primary w-full justify-center text-base py-4">
            Join Our Club
          </NavLink>
        </div>

        <div className="mt-auto pb-10 flex items-center justify-center">
          <img
            src="/images/membertou-boxing-logo-white.svg"
            alt=""
            aria-hidden="true"
            width={360}
            height={218}
            decoding="async"
            className="block w-full max-w-[360px] h-auto"
            style={{ opacity: 0.15 }}
          />
        </div>
      </nav>
    </div>
  );
}
